"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useStore, useUploadSessions } from "@/stores/appStore";
import { UploadItem } from "../core/types";
import {
  AlertCircleIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LoaderIcon,
  NonPDFDocIcon,
  PDFDocIcon,
  XIcon,
} from "@/components/fun-ui/icons";
import { IconButton } from "@/components/icon-button";
import { getUploadMessages } from "../locales/messages";
import * as DismissableLayer from "@radix-ui/react-dismissable-layer";
import { ScrollableContainer } from "@/components/ui/scrollable-container";

const messages = getUploadMessages();

type PanelStatus = "uploading" | "success" | "error";


/**
 * 全局文献上传进度面板。
 *
 * 设计要点：
 * - 单一数据源：通过 useUploadSessions().getAllUploadTasks() 直接订阅 uploadSessionSlice
 * - 生命周期：
 *   1）当 tasks.length > 0 时自动显示面板（visible=true）
 *   2）当上一轮所有任务都完成，且有新任务加入时，自动展开面板（fold=false，visible=true）
 *   3）点击「关闭」按钮会 clearAllUploadSessions() 并隐藏面板
 *
 * 状态规则：
 * - PanelStatus:
 *   - 若存在任意 status === "error" 的任务，头部显示「部分失败」
 *   - 否则若全部为 success/warning，显示「上传成功」
 *   - 否则视为「上传中」
 *
 * - 行内状态（UploadProgressRow）：
 *   - DUPLICATE：status:"warning" + error.code:"DUPLICATE"，绿色提示「重复文献，已自动合并」
 *   - FORMAT_INVALID：status:"error" + error.code:"FORMAT_INVALID"，红色提示「非 PDF 格式文件，无法上传」
 *   - SIZE_EXCEEDED / COUNT_EXCEEDED：status:"error"，红色提示对应文案
 *   - 其它错误：status:"error" + SERVER_ERROR，红色「文献上传失败，请重试」
 *
 * 注意：
 * - 面板自身不再维护任何上传逻辑，只根据 UploadItem 的字段进行纯展示，
 *   所有状态变更都由 useUpload + uploadSessionSlice 驱动。
 */
export function UploadProgressPanel({ className }: { className?: string }) {
  const { getAllUploadTasks, activeUploadSessionId } = useUploadSessions();
  const clearAllUploadSessions = useStore((state) => state.clearAllUploadSessions);
  // 直接订阅 uploadSessions 以确保组件在 store 更新时重新渲染
  const uploadSessions = useStore((state) => state.uploadSessions);
  const tasks: UploadItem[] = useMemo(() => {
    const allTasks = Object.values(uploadSessions).flatMap((session) => session.tasks);
    // 去重：使用 Map 确保每个任务 ID 只出现一次，保留最新的任务
    // 优先级：1. 时间戳（endTime > startTime） 2. 状态完成度（success > warning > error > uploading > validating）
    const taskMap = new Map<string, UploadItem>();
    const statusPriority: Record<string, number> = {
      success: 5,
      warning: 4,
      error: 3,
      uploading: 2,
      validating: 1,
      processing: 2,
    };
    
    for (const task of allTasks) {
      const existing = taskMap.get(task.id);
      if (!existing) {
        taskMap.set(task.id, task);
      } else {
        // 优先比较时间戳
        const existingTime = existing.endTime ?? existing.startTime ?? 0;
        const currentTime = task.endTime ?? task.startTime ?? 0;
        
        if (currentTime > existingTime) {
          taskMap.set(task.id, task);
        } else if (currentTime === existingTime) {
          // 时间戳相同时，优先保留状态更"完成"的任务
          const existingPriority = statusPriority[existing.status] || 0;
          const currentPriority = statusPriority[task.status] || 0;
          if (currentPriority > existingPriority) {
            taskMap.set(task.id, task);
          }
        }
      }
    }
    return Array.from(taskMap.values());
  }, [uploadSessions]);
  const [fold, setFold] = useState(false);
  const [visible, setVisible] = useState(tasks.length > 0);
  const panelRef = useRef<HTMLDivElement>(null);
  // 记录上一次的任务状态，用于判断"旧任务完成后又有新任务"场景
  const prevStateRef = useRef({
    hadTasks: tasks.length > 0,
    allCompleted: tasks.every(isTaskCompleted),
    length: tasks.length,
  });


  useEffect(() => {
    if (tasks.length > 0) {
      setVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks.length]);


  useEffect(() => {
    const currentHasTasks = tasks.length > 0;
    const currentAllCompleted = tasks.every(isTaskCompleted);
    const prev = prevStateRef.current;

    const startedNewBatch =
      currentHasTasks &&
      (!prev.hadTasks || prev.allCompleted) &&
      (!currentAllCompleted || tasks.length > prev.length);

    if (startedNewBatch) {
      setFold(false);
      setVisible(true);
    }

    prevStateRef.current = {
      hadTasks: currentHasTasks,
      allCompleted: currentAllCompleted,
      length: tasks.length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const displayTasks = tasks;

  const panelStatus: PanelStatus = useMemo(() => {
    // 检查是否所有任务都已完成（success / warning / error）
    const allCompleted = displayTasks.every((task) =>
      task.status === "success" || task.status === "warning" || task.status === "error"
    );

    // 只要还有任务未完成，就显示"上传中"
    if (!allCompleted) {
      return "uploading";
    }

    // 所有任务都完成后，判断最终状态
    if (displayTasks.some((task) => task.status === "error")) {
      return "error";
    }

    // 全部成功（包括 warning，因为 warning 渲染为 success 样式）
    return "success";
  }, [displayTasks]);

  // 判断是否全部失败
  const allFailed = useMemo(() => {
    if (displayTasks.length === 0) return false;
    return displayTasks.every((task) => task.status === "error");
  }, [displayTasks]);

  // 计算完成数：只包括 success 和 warning（error 不计入完成数）
  const completedCount = useMemo(() => {
    return displayTasks.filter(
      (task) => task.status === "success" || task.status === "warning"
    ).length;
  }, [displayTasks]);
  
  // 调试：找出未完成的任务（状态不是 success/warning/error）
  const incompleteTasks = useMemo(() => {
    return displayTasks.filter(
      (task) => task.status !== "success" && task.status !== "warning" && task.status !== "error"
    );
  }, [displayTasks]);
  
  // 开发环境下输出调试信息
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      // 统计所有任务的状态
      const statusCount = displayTasks.reduce((acc, task) => {
        acc[task.status] = (acc[task.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      // 如果计数不匹配，输出详细信息
      if (completedCount !== tasks.length) {
        console.warn("[UploadProgressPanel] 计数不匹配:", {
          totalTasks: tasks.length,
          completedCount,
          statusCount,
          incompleteTasks: incompleteTasks.map(t => ({
            id: t.id,
            status: t.status,
            fileName: t.file.name,
            progress: t.progress,
            endTime: t.endTime,
            startTime: t.startTime,
          })),
          allTasks: displayTasks.map(t => ({
            id: t.id,
            status: t.status,
            fileName: t.file.name,
            progress: t.progress,
          })),
        });
      }
      
      // 如果有未完成的任务，也输出
      if (incompleteTasks.length > 0) {
        console.warn("[UploadProgressPanel] 发现未完成的任务:", incompleteTasks.map(t => ({
          id: t.id,
          status: t.status,
          fileName: t.file.name,
          progress: t.progress,
        })));
      }
    }
  }, [incompleteTasks, completedCount, tasks.length, displayTasks]);

  if (!visible || !tasks.length) {
    return null;
  }

  const handleClose = () => {
    clearAllUploadSessions();
    setVisible(false);
    // 重置 prevStateRef，确保下次有新任务时能正确显示面板
    prevStateRef.current = {
      hadTasks: false,
      allCompleted: true,
      length: 0,
    };
  };

  return (
    <DismissableLayer.Branch
      ref={panelRef}
      data-upload-progress-panel
      className={cn(
        "fixed bottom-4 right-4 z-[60] rounded border bg-background shadow px-5 py-3 w-[400px] pointer-events-auto",
        className,
      )}
    >
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-foreground">
          {getStatusIcon(panelStatus, 20)}
          <span>{getStatusLabel(panelStatus, undefined, allFailed)}</span>
          <span className="text-annotation-foreground">（{completedCount}/{tasks.length}）</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-annotation-foreground">
          <IconButton
            label={fold ? messages.progress.expand : messages.progress.collapse}
            onClick={() => setFold((prev) => !prev)}
            Icon={fold ? ChevronUpIcon : ChevronDownIcon}
          />
          <IconButton
            label={messages.common.close}
            onClick={handleClose}
            Icon={XIcon}
          />
        </div>
      </header>
      {panelStatus === "uploading" && (
        <div className="text-xs text-annotation-foreground mt-1">{messages.progress.doNotCloseOrRefresh}</div>
      )}
      {!fold && (
        <ScrollableContainer className="mt-3 max-h-48 overflow-y-auto space-y-3 pr-2 text-xs">
          {displayTasks.map((task) => (
            <UploadProgressRow key={task.id} task={task} />
          ))}
        </ScrollableContainer>
      )}
    </DismissableLayer.Branch>
  );
}

function UploadProgressRow({ task }: { task: UploadItem }) {
  const totalSizeMB = task.file.size / (1024 * 1024);
  const totalSizeText = totalSizeMB.toFixed(2);
  const currentSizeMB =
    task.status === "success" || task.status === "warning"
      ? totalSizeMB
      : (totalSizeMB * (task.progress ?? 0)) / 100;
  const currentSizeText = currentSizeMB.toFixed(2);
  const statusLabel = getStatusLabel(task.status, task.error?.code);
  const isDuplicate = task.status === "warning" && task.error?.code === "DUPLICATE";
  const isFormatInvalid = task.status === "error" && task.error?.code === "FORMAT_INVALID";

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex flex-row items-center gap-2 flex-1 min-w-0">
        {isFormatInvalid ? <NonPDFDocIcon size={20} /> : <PDFDocIcon size={20} />}
        <div className="flex flex-col w-full space-y-1 min-w-0">
          <span className="truncate text-xs font-medium">{task.file.name}</span>
          {/* 优先显示重复提示，然后是错误提示，最后是文献大小 */}
          {isDuplicate && task.error?.message ? (
            <span className="text-constructive text-[10px]">{task.error.message}</span>
          ) : task.status === "error" && task.error?.message ? (
            <span className="text-destructive text-[10px]">{task.error.message}</span>
          ) : (
            <span className="text-annotation-foreground text-[10px]">
              {currentSizeText}MB / {totalSizeText}MB
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 w-20 justify-end">
        {getStatusIcon(task.status === "warning" ? "success" : task.status)}
        <span
          className={cn(
            "inline-block text-xs shrink-0",
            "text-annotation-foreground",
            task.status === "error" && "text-destructive",
            (task.status === "success" || task.status === "warning") && "text-constructive",
          )}
        >
          {statusLabel}
        </span>
      </div>
    </div>
  );
}

function isTaskCompleted(task: UploadItem) {
  return task.status === "success" || task.status === "warning" || task.status === "error";
}

function getStatusIcon(status: PanelStatus | UploadItem["status"], size: number = 14) {
  if (status === "error") {
    return <AlertCircleIcon size={size} className="text-destructive shrink-0" />;
  }
  if (status === "success" || status === "warning") {
    // warning 也使用 success 图标，但样式保持一致（text-constructive）
    return <CheckCircleIcon size={size} className="text-constructive shrink-0" />;
  }
  return <LoaderIcon size={size} className="text-primary animate-spin shrink-0" />;
}

function getStatusLabel(status: PanelStatus | UploadItem["status"], errorCode?: string, allFailed?: boolean) {
  if (status === "error") {
    // 头部 summary（无 errorCode）
    if (!errorCode) {
      // 如果全部失败，显示"上传失败"；否则显示"上传完成，部分异常"
      return allFailed ? messages.progress.allFailed : messages.progress.partialFailed;
    }
    // 具体行（有 errorCode）显示"上传失败"
    return messages.progress.failedDetail;
  }
  if (status === "success" || status === "warning") {
    // 重复文献显示成功文案，但使用 text-constructive 样式
    if (errorCode === "DUPLICATE" || status === "warning") {
      return messages.progress.success; // 或使用 "重复文献，已自动合并"
    }
    return messages.progress.success;
  }
  return messages.progress.uploading;
}
